import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Tag, ArrowRight, FileText } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import PageFrame from '../components/PageFrame';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category icons and descriptions
const CATEGORY_INFO = {
  'Accident Prevention': {
    icon: '🛡️',
    description: 'Tips and strategies to avoid accidents before they happen',
    color: 'from-green-600 to-green-700'
  },
  'Legal Guidance': {
    icon: '⚖️',
    description: 'Understanding your rights and legal options after an accident',
    color: 'from-blue-600 to-blue-700'
  },
  'Safety Tips': {
    icon: '✓',
    description: 'Practical advice for staying safe on the road',
    color: 'from-orange-600 to-orange-700'
  },
  'Industry News': {
    icon: '📰',
    description: 'Latest updates from the trucking industry',
    color: 'from-purple-600 to-purple-700'
  },
  'Driver Wellness': {
    icon: '❤️',
    description: 'Health and wellness tips for professional drivers',
    color: 'from-red-600 to-red-700'
  },
  'Equipment & Maintenance': {
    icon: '🔧',
    description: 'Keeping your truck in top condition',
    color: 'from-yellow-600 to-yellow-700'
  },
  'Regulations & Compliance': {
    icon: '📋',
    description: 'Staying compliant with DOT and FMCSA regulations',
    color: 'from-indigo-600 to-indigo-700'
  },
  'Insurance': {
    icon: '📑',
    description: 'Understanding trucking insurance and claims',
    color: 'from-teal-600 to-teal-700'
  }
};

const BlogCategories = () => {
  const [categories, setCategories] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      // Fetch categories
      const catResponse = await axios.get(`${API}/admin/blog/categories`);
      const cats = catResponse.data?.categories || catResponse.data || [];
      setCategories(Array.isArray(cats) ? cats : []);

      // Fetch posts to count per category
      const postsResponse = await axios.get(`${API}/admin/blog/posts?published_only=true&limit=100`);
      const posts = postsResponse.data.posts || postsResponse.data || [];
      
      // Count posts per category
      const counts = {};
      posts.forEach(post => {
        const cat = post.category || 'Uncategorized';
        counts[cat] = (counts[cat] || 0) + 1;
      });
      setCategoryCounts(counts);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryInfo = (category) => {
    return CATEGORY_INFO[category] || {
      icon: '📁',
      description: `Articles about ${category.toLowerCase()}`,
      color: 'from-gray-600 to-gray-700'
    };
  };

  if (loading) {
    return (
      <PageFrame>
        <div className="min-h-screen bg-gray-900 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      </PageFrame>
    );
  }

  return (
    <PageFrame>
      <SEO
        title="Blog Categories | TruckAccidentSupport"
        description="Browse our trucking blog by category. Find articles on accident prevention, legal guidance, safety tips, driver wellness, and more."
        canonical="/blog/categories"
      />

      <div className="min-h-screen bg-gray-900">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Tag className="h-6 w-6 text-orange-500" />
                <span className="text-orange-500 font-semibold uppercase tracking-wider text-sm">
                  Browse by Topic
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Blog Categories
              </h1>
              <p className="text-xl text-gray-400">
                Find the information you need organized by topic. From accident prevention 
                to legal guidance, we've got you covered.
              </p>
            </div>
          </div>
        </section>

        {/* Categories Grid */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {categories.map((category) => {
                const info = getCategoryInfo(category);
                const count = categoryCounts[category] || 0;
                
                return (
                  <Link 
                    key={category} 
                    to={`/blog?category=${encodeURIComponent(category)}`}
                    className="block group"
                  >
                    <Card className="bg-gray-800 border-gray-700 h-full overflow-hidden hover:border-orange-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-orange-500/10">
                      <div className={`h-2 bg-gradient-to-r ${info.color}`}></div>
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="text-4xl">{info.icon}</div>
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-white group-hover:text-orange-500 transition-colors">
                              {category}
                            </h3>
                            <p className="text-gray-400 text-sm mt-2 mb-4">
                              {info.description}
                            </p>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-500">
                                <FileText className="h-4 w-4 inline mr-1" />
                                {count} {count === 1 ? 'article' : 'articles'}
                              </span>
                              <ArrowRight className="h-5 w-5 text-orange-500 opacity-0 group-hover:opacity-100 transform translate-x-0 group-hover:translate-x-1 transition-all" />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>

            {categories.length === 0 && (
              <div className="text-center py-16">
                <Tag className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-400 mb-2">No Categories Yet</h3>
                <p className="text-gray-500">Check back soon for categorized content.</p>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-orange-600 to-orange-700">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Can't Find What You're Looking For?
            </h2>
            <p className="text-orange-100 mb-8 max-w-2xl mx-auto">
              Browse all our articles or use the search feature to find specific topics.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/blog">
                <Button className="bg-white text-orange-600 hover:bg-gray-100 font-semibold px-6">
                  View All Articles
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 font-semibold px-6">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </PageFrame>
  );
};

export default BlogCategories;
